import type { MemoryListByFilterData } from "../contracts/memory.js";
import type { ScheduledTaskView } from "../contracts/scheduling.js";
import type { SessionSummaryView } from "../contracts/sessions.js";

const MEMORY_COUNT_PAGE_LIMIT = 100;

/**
 * Returns the newest valid activity timestamp exposed by the conversation
 * summaries. `accessedAt` advances with the persisted conversation, so this
 * covers the latest turn without issuing one history request per row.
 */
export function mostRecentConversationActivity(
  conversations: readonly SessionSummaryView[],
): string | null {
  let newest: { timestamp: string; epochMs: number } | null = null;

  for (const conversation of conversations) {
    const epochMs = Date.parse(conversation.accessedAt);
    if (!Number.isFinite(epochMs)) continue;
    if (!newest || epochMs > newest.epochMs) {
      newest = { timestamp: conversation.accessedAt, epochMs };
    }
  }

  return newest?.timestamp ?? null;
}

/** Match the Scheduled jobs tab: reminders are session-local, not background jobs. */
export function activeScheduleCount(tasks: readonly ScheduledTaskView[]): number {
  return tasks.filter((task) => task.type === "job" && task.status === "active").length;
}

/** Count the complete cursor chain; a first page is not a trustworthy total. */
export async function countMemoryEntries(
  firstPage: MemoryListByFilterData,
  loadPage: (cursor: string, limit: number) => Promise<MemoryListByFilterData>,
): Promise<number> {
  let total = firstPage.entities.length;
  let cursor = firstPage.nextCursor;
  const seenCursors = new Set<string>();

  while (cursor) {
    if (seenCursors.has(cursor)) {
      throw new Error("Memory list returned a repeated pagination cursor");
    }
    seenCursors.add(cursor);
    const page = await loadPage(cursor, MEMORY_COUNT_PAGE_LIMIT);
    total += page.entities.length;
    cursor = page.nextCursor;
  }

  return total;
}
